import React, { useState, useCallback, useMemo } from "react";
import {
  Button,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Search, Close } from "@mui/icons-material";
import { FeedbackDialog } from "./FeedbackDialog";
import {
  addDashboardV2,
  createNewDashboardV2,
  getDashboardList,
} from "Samarth Module/api/samarthAiAPI";
import ReusableSnackbar from "./ReusableSnackbar";
import TableImg from "../../../assets/images/screenshot1.svg";
import GraphsImg from "../../../assets/images/screenshot2.svg";
import { useLocation, useParams } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";

// Module-level cache: keyed by conversation ID so all dropdown instances share one fetch
const dashboardCache = {
  conversationId: null,
  data: null,
  promise: null,
};

// Extracted static styles to prevent re-creation on each render
const styles = {
  button: {
    textTransform: "none",
    borderRadius: "29px",
    padding: "12px 20px",
    border: "1px solid #0066CC",
    fontSize: "14px",
    fontWeight: 400,
    "&:hover": {
      backgroundColor: "#c2e0ff",
      color: "#004D99",
      border: "1px solid #004D99",
    },
    "@media (max-width:1024px)": {
      fontSize: "12px",
      borderRadius: "20px",
      padding: "8px 16px",
      height: "30px",
    },
    "@media (min-width:1025px) and (max-width:1270px)": {
      fontSize: "13px",
      borderRadius: "24px",
      padding: "10px 18px",
      height: "36px",
    },
    "@media (min-width:1271px)": {
      fontSize: "14px",
      borderRadius: "29px",
      padding: "12px 20px",
      height: "44px",
    },
  },
  paper: {
    width: 280,
    borderRadius: "12px",
    mt: 2,
    background: "linear-gradient(180deg, #C2E0FF -119.85%, #FFFFFF 100%)",
    boxShadow: "0px 0px 20px 0px #0000001F",
  },
  scrollBox: {
    maxHeight: 192,
    overflowY: "auto",
    "&::-webkit-scrollbar": { width: "6px" },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#DFDFDF",
      borderRadius: "6px",
    },
    scrollbarWidth: "thin",
    scrollbarColor: "#DFDFDF transparent",
  },
  checkbox: {
    p: 0,
    mr: 1,
    "&:hover .MuiBox-root": {
      borderColor: "#354ACD",
      backgroundColor: "#EEF3FF",
    },

    "&.Mui-checked:hover .MuiBox-root": {
      backgroundColor: "#354ACD",
    },

    "&.Mui-disabled:hover .MuiBox-root": {
      borderColor: "#D9E1E7",
      backgroundColor: "#F5F7FA",
    },
  },
  createBtn: {
    background: "#0066CC",
    color: "#FFFFFF",
    fontWeight: 400,
    fontSize: "14px",
    borderRadius: "29px",
    padding: "12px 20px",
    margin: "0 8px 4px 8px",
    justifyContent: "center",
    mt: 2,
    border: "1px solid #0066CC",
    "&:hover": {
      backgroundColor: "#004D99",
      color: "#FFFFFF",
      border: "1px solid #004D99",
    },
  },
  textField: {
    mb: 2,
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      "& fieldset": { borderColor: "#B3C5D4" },
      "&:hover fieldset": { borderColor: "#B3C5D4" },
      "&.Mui-focused fieldset": { borderColor: "#0066CC", borderWidth: "1px" },
    },
  },
};

const DashboardDropdown = ({
  initialDashboards = [],
  messageId,
  viewtype,
  columnCount = 0,
  graphData,
  conversationId,
}) => {
  const { id } = useParams();
  const location = useLocation();
  const userInfo = useMemo(
    () => JSON.parse(localStorage.getItem("UserInfo"))?.user_info,
    [],
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [dashboards, setDashboards] = useState(
    dashboardCache.conversationId === id && dashboardCache.data
      ? dashboardCache.data
      : [],
  );
  const [selectedDashboards, setSelectedDashboards] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);

  const handleCloseMenu = useCallback(() => setAnchorEl(null), []);

  const handleToggleDashboard = useCallback((id) => {
    setSelectedDashboards((prev) => (prev === id ? null : id));
  }, []);

  const handleOpenDialog = useCallback(() => {
    setSelectedDashboards(null);
    setOpenDialog(true);
  }, []);
  const getGraphViewSize = (graphData) => {
    if (!graphData) return 6;

    const type = graphData?.type;
    const dataLength = graphData?.data?.length || 0;

    switch (type) {
      case "pieChart":
        return dataLength <= 5 ? 6 : 12;

      case "barGraph":
        return dataLength > 8 ? 12 : 6;

      case "multiLineGraphs":
        return 12; // always wide (multiple lines)

      case "composedBarLineChart":
        return dataLength > 20 ? 12 : 6; // complex chart → full width

      case "heatMap":
        return dataLength > 20 ? 12 : 6;

      default:
        return 6;
    }
  };
  const handleAddDashboards = useCallback(async () => {
    try {
      const payload = {
        dashboard_id: selectedDashboards,
        user_id: userInfo.id,
        user_name: userInfo.name,
        user_email: userInfo.email,
        message_id: messageId,
        conversation_id: id || conversationId,
        view_type: viewtype,
        view_size:
          viewtype === "table"
            ? columnCount > 4
              ? 12
              : columnCount <= 3
                ? 4
                : 6
            : viewtype === "graph"
              ? getGraphViewSize(graphData)
              : 4,
      };

      await addDashboardV2(payload);
      setSelectedDashboards(null);
      setSuccessMessage("Added to Dashboard Successfully");
      setSuccess(true);
      setAnchorEl(null);
    } catch (error) {
      console.error("Failed to add dashboards:", error);
      setError("Failed to add dashboard. Please try again.");
    }
  }, [selectedDashboards]);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setNewDashboardName("");
  }, []);

  const fetchDashboardCards = useCallback(
    async (force = false) => {
      if (!userInfo?.id) return;

      if (
        !force &&
        dashboardCache.conversationId === id &&
        dashboardCache.data
      ) {
        setDashboards(dashboardCache.data);
        return;
      }

      if (
        !force &&
        dashboardCache.conversationId === id &&
        dashboardCache.promise
      ) {
        setLoading(true);
        try {
          const data = await dashboardCache.promise;
          setDashboards(data);
        } finally {
          setLoading(false);
        }
        return;
      }

      setLoading(true);

      const payload = {
        user_id: userInfo.id,
        user_name: userInfo.name,
        user_email: userInfo.email,
      };

      const fetchPromise = getDashboardList(payload).then((response) => {
        const formattedCards = (response.dashboard_list || []).map((item) => ({
          id: item.dashboard_id,
          title: item.dashboard_title,
          image: item.type === "table" ? TableImg : GraphsImg,
        }));

        dashboardCache.conversationId = id;
        dashboardCache.data = formattedCards;
        dashboardCache.promise = null;

        return formattedCards;
      });

      dashboardCache.conversationId = id;
      dashboardCache.promise = fetchPromise;

      try {
        const formattedCards = await fetchPromise;
        setDashboards(formattedCards);
      } catch (error) {
        console.error("Error fetching dashboard list:", error);
        dashboardCache.promise = null;
      } finally {
        setLoading(false);
      }
    },
    [userInfo, id],
  );

  const handleCreateDashboard = useCallback(async () => {
    const name = newDashboardName.trim();

    if (!name) {
      setError("Dashboard name cannot be empty");
      return;
    }

    if (!userInfo?.id) {
      setError("User info missing");
      return;
    }

    const existingTitles = new Set(
      dashboards.map((d) => (d.title || "").trim().toLowerCase()),
    );
    let uniqueName = name;
    if (existingTitles.has(name.toLowerCase())) {
      let i = 1;
      while (existingTitles.has(`${name} (${i})`.toLowerCase())) i++;
      uniqueName = `${name} (${i})`;
    }

    const payload = {
      dashboard_title: uniqueName,
      user_id: userInfo.id,
      user_name: userInfo.name,
      user_email: userInfo.email,
      dashboard_description: "dashboard description",
    };

    try {
      setLoading(true);

      const res = await createNewDashboardV2(payload);
      setOpenDialog(false);

      // Force re-fetch to include the new dashboard (invalidates cache)
      await fetchDashboardCards(true);

      // `createNewDashboardV2` returns the full axios response, so the id (if
      // present) lives under `data`. As a robust fallback, look it up from
      // the refreshed cache by the unique title we just submitted — that
      // matches whatever id format `getDashboardList` returns.
      const apiNewId =
        res?.data?.dashboard_id ?? res?.data?.data?.dashboard_id ?? null;
      const refreshedList = dashboardCache.data || [];
      const matched = refreshedList.find(
        (d) =>
          (d.title || "").trim().toLowerCase() === uniqueName.toLowerCase(),
      );
      const newId = matched?.id ?? apiNewId;
      if (newId != null) setSelectedDashboards(newId);

      setSuccessMessage(`${uniqueName} Created Successfully`);
      setSuccess(true);
      setNewDashboardName("");
    } catch (err) {
      console.error("Error creating dashboard:", err);
      setError("Failed to create dashboard. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [newDashboardName, userInfo, dashboards, fetchDashboardCards]);

  const filteredDashboards = useMemo(
    () =>
      dashboards
        .filter((d) => d.title.toLowerCase().includes(searchTerm.toLowerCase()))
        .map((d) => ({
          id: d.id,
          title: d.title,
        })),
    [dashboards, searchTerm],
  );
  const handleOpenMenu = async (event) => {
    setAnchorEl(event.currentTarget);
    setSelectedDashboards(null);
    await fetchDashboardCards();
  };
  return (
    <>
      <Button onClick={handleOpenMenu} sx={styles.button}>
        Add to Dashboard
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        PaperProps={{ sx: styles.paper }}
      >
        <Typography
          sx={{
            padding: "8px 16px",
            fontWeight: 600,
            color: "#4D5F6E",
            mb: 2,
            fontSize: "18px",
          }}
        >
          Select a dashboard
        </Typography>

        <Box sx={{ px: 0.5, pb: 1 }}>
          <TextField
            size="small"
            placeholder="Search"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={styles.textField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    sx={{
                      color: searchTerm ? "#1A2025" : "#9CA3AF",
                      fontSize: 20,
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm("")}
                    sx={{ p: "4px" }}
                  >
                    <Close sx={{ fontSize: 18, color: "#0066CC" }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={styles.scrollBox}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 2,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : filteredDashboards.length === 0 ? (
            <Typography
              sx={{
                px: 2,
                py: 1,
                fontSize: 14,
                display: "flex",
                justifyContent: "center",
              }}
            >
              No dashboards found
            </Typography>
          ) : (
            filteredDashboards.map((dashboard) => (
              <MenuItem
                key={dashboard.id}
                onClick={() => handleToggleDashboard(dashboard.id)}
                sx={{
                  px: 1.5,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Checkbox
                  checked={selectedDashboards === dashboard.id}
                  disableRipple
                  icon={
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        border: "1px solid #B3C5D4",
                        borderRadius: "6px",
                        backgroundColor: "#fff",
                      }}
                    />
                  }
                  checkedIcon={
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        border: "1px solid #354ACD",
                        borderRadius: "6px",
                        backgroundColor: "#354ACD",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 16, color: "#fff" }} />
                    </Box>
                  }
                  sx={styles.checkbox}
                />
                <ListItemText
                  primary={dashboard.title}
                  primaryTypographyProps={{
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "#4D5F6E !important",
                  }}
                />
              </MenuItem>
            ))
          )}
        </Box>

        <MenuItem
          onClick={
            selectedDashboards
              ? handleAddDashboards // your custom add logic
              : handleOpenDialog
          }
          sx={styles.createBtn}
        >
          {selectedDashboards ? "Add" : "Create New Dashboard"}
        </MenuItem>
      </Menu>

      <FeedbackDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title="Create New Dashboard"
        feedbackComment={newDashboardName}
        setFeedbackComment={setNewDashboardName}
        onSubmit={handleCreateDashboard}
        loading=""
        buttonLabel="Create Dashboard"
        minRows={1}
        placeholder="Enter Dashboard"
      />
      <ReusableSnackbar
        open={success}
        message={successMessage}
        type="success"
        onClose={() => setSuccess(false)}
      />

      <ReusableSnackbar
        open={!!error}
        message={error}
        type="error"
        onClose={() => setError("")}
      />
    </>
  );
};

export default DashboardDropdown;
